<script setup>
import { computed, nextTick, ref } from 'vue'
import { useFloating } from '@/composables/useFloating.js'

/**
 * A choice of one among many whose names are long (a catalogue's components): what is chosen
 * shows in full, its lines wrapped — the field as wide as its column, not as its longest option —
 * and a click opens a search over the options, each wrapped too, with its hint under it and its
 * group's heading above. Arrows and Enter pick, Escape closes.
 *
 * options: [{ value, label, hint?, group? }]
 */
const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  disabled: Boolean,
  required: Boolean,
})
const emit = defineEmits(['update:modelValue'])
const query = ref('')
const active = ref(0)
const search = ref(null)
const { open, anchor, floating, style, show, close } = useFloating()
const fold = (v) => String(v || '').toLowerCase().replace(/ł/g, 'l').normalize('NFD').replace(/[̀-ͯ]/g, '')
const chosen = computed(() => props.options.find((o) => o.value === props.modelValue) || null)
const found = computed(() => {
  const words = fold(query.value).split(/\s+/).filter(Boolean)
  return words.length ? props.options.filter((o) => words.every((w) => fold(`${o.group || ''} ${o.label} ${o.hint || ''}`).includes(w))) : props.options
})
// The found ones under their groups' headings; "none" first when the choice may be empty.
const rows = computed(() => {
  const out = props.required ? [] : [{ option: { value: null, label: '—' }, key: 'none' }]
  let group = null
  for (const o of found.value) {
    if (o.group && o.group !== group) out.push({ heading: o.group, key: `g:${o.group}` })
    group = o.group || null
    out.push({ option: o, key: `o:${o.value}` })
  }
  return out
})
const picks = computed(() => rows.value.filter((r) => r.option))
async function openList() {
  if (props.disabled) return
  query.value = ''
  active.value = Math.max(0, picks.value.findIndex((r) => r.option.value === props.modelValue))
  await show()
  await nextTick()
  search.value?.focus()
  floating.value?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
}
function pick(o) {
  emit('update:modelValue', o.value)
  close()
  anchor.value?.focus()
}
function key(e) {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    active.value = Math.min(Math.max(0, active.value + (e.key === 'ArrowDown' ? 1 : -1)), picks.value.length - 1)
    nextTick(() => floating.value?.querySelector('[data-active]')?.scrollIntoView({ block: 'nearest' }))
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const r = picks.value[active.value]
    if (r) pick(r.option)
  }
}
const typed = () => (active.value = props.required || !query.value ? 0 : 1)
</script>

<template>
  <button ref="anchor" type="button" class="input flex w-full items-start gap-2 py-1 text-left text-sm whitespace-normal" :disabled="disabled" aria-haspopup="listbox" :aria-expanded="open" @click="open ? close() : openList()">
    <span class="min-w-0 flex-1 break-words">
      <template v-if="chosen">{{ chosen.label }}<span v-if="chosen.hint" class="block text-xs text-muted">{{ chosen.hint }}</span></template>
      <span v-else class="text-faint">{{ modelValue ? modelValue : placeholder || '—' }}</span>
    </span>
    <i class="fa-solid fa-chevron-down mt-1 shrink-0 text-xs text-faint" aria-hidden="true"></i>
  </button>
  <Teleport to="body">
    <div v-if="open" ref="floating" :style="style" class="fixed flex max-h-[22rem] w-[min(28rem,calc(100vw-1rem))] flex-col rounded-lg border border-default bg-[var(--surface)] shadow-lg" data-testid="choice-picker">
      <div class="border-b border-default p-2">
        <input ref="search" v-model="query" class="input py-1 text-sm" :placeholder="$t('common.searchPlaceholder')" @input="typed" @keydown="key" />
      </div>
      <ul class="min-h-0 flex-1 overflow-y-auto py-1" role="listbox">
        <template v-for="r in rows" :key="r.key">
          <li v-if="r.heading" class="px-3 pt-2 pb-0.5 text-xs font-semibold text-muted">{{ r.heading }}</li>
          <li v-else role="option" :aria-selected="r.option.value === modelValue" :data-active="picks[active] === r ? '' : null"
              :class="['cursor-pointer px-3 py-1.5 text-sm break-words', picks[active] === r ? 'bg-[var(--surface-3)]' : '', r.option.value === modelValue ? 'font-medium text-brand-600' : '']"
              @mouseenter="active = picks.indexOf(r)" @mousedown.prevent="pick(r.option)">
            {{ r.option.label }}<span v-if="r.option.hint" class="block text-xs font-normal text-muted">{{ r.option.hint }}</span>
          </li>
        </template>
        <li v-if="!found.length" class="px-3 py-2 text-sm text-faint">{{ $t('common.nothingFound') }}</li>
      </ul>
    </div>
  </Teleport>
</template>
